// SPDX-License-Identifier: AGPL-3.0-only
/**
 * Config-migration runner.
 *
 * Each migration is a pure function `(doc) => doc` upgrading one version
 * step. The server runs the chain at boot and lazily on read; zip import
 * replays the same path so old exports always upgrade, never silently drop.
 *
 * Browser-safe: no node: imports (this module ships in
 * `@adminium/engine/config`).
 */
/** The current (latest) config schema version shipped with this build. */
export const CONFIG_VERSION = 1;
/**
 * Registered migrations, ordered by `from`. Empty while v1 is current; the
 * first real entry arrives with the v1→v2 bump.
 */
export const configMigrations = [];
export class ConfigMigrationError extends Error {
    name = 'ConfigMigrationError';
}
/** Latest version reachable through `migrations` (>= CONFIG_VERSION). */
export function latestConfigVersion(migrations = configMigrations) {
    return migrations.reduce((max, m) => Math.max(max, m.to), CONFIG_VERSION);
}
/**
 * Upgrades `doc.v` to the latest version by running the migration chain.
 * Returns a new document — the input is never mutated. Throws
 * `ConfigMigrationError` when the document has no usable version, is newer
 * than this build understands (the boot downgrade guard's condition), or a
 * step in the chain is missing.
 */
export function runConfigMigrations(doc, migrations = configMigrations) {
    if (typeof doc !== 'object' || doc === null || Array.isArray(doc)) {
        throw new ConfigMigrationError('config document must be an object');
    }
    const version = doc['v'];
    if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
        throw new ConfigMigrationError(`config document has no valid version at "v" (got ${JSON.stringify(version)})`);
    }
    const target = latestConfigVersion(migrations);
    if (version > target) {
        throw new ConfigMigrationError(`config document version ${String(version)} is newer than the latest supported version ` +
            `${String(target)} — upgrade Adminium to open it`);
    }
    let current = { ...doc, v: version };
    while (current.v < target) {
        const from = current.v;
        const step = migrations.find((m) => m.from === from);
        if (!step) {
            throw new ConfigMigrationError(`no config migration registered from version ${String(from)}`);
        }
        if (!Number.isInteger(step.to) || step.to <= step.from) {
            throw new ConfigMigrationError(`invalid migration step v${String(step.from)}→v${String(step.to)}`);
        }
        // Clone before handing to the migration so a misbehaving (mutating)
        // migration can never corrupt the caller's document.
        const upgraded = step.migrate(structuredClone(current));
        current = { ...upgraded, v: step.to };
    }
    return current;
}
//# sourceMappingURL=migrations.js.map